<!--
  StoreCheckout.vue

  PR-G (2026-06-22) Checkout IA refactor; updated by PR E (retire legacy
  shopper-allowance card + columns).

  Orchestrator component for the Checkout section. Mounts:

    1. StoreCheckoutInfo (Identity / Custom fields / Shipping cards).
       This is the legacy "Checkout Fields" surface unchanged in
       behavior; it owns the field-flag and custom-dropdown plumbing.

    2. StoreOrderLimits (Store Limits & Security) -- identity, custom
       data, shipping mode, and order limits.

  PR E removed the legacy "Offer Shopper Allowance" card
  (StoreShopperAllowance) that wrote the flat per-store
  ``company_allowance`` / ``company_allowance_personal_pay`` columns. The
  v2 successor (StoreAllowancePolicy, "Shopper Allowance") now lives on the
  Pricing section and is the single source of allowance config.

  StoreAddEdit mounts this with ``ref="checkoutInfo"``. The parent's
  autosave + validation loop calls updateCampaign(), validateTab(),
  loadComponent(), and getUsedCustomDropdownOptions() on that ref;
  this orchestrator forwards each call to its inner refs so the
  legacy plumbing keeps working without churn.

  Cross-card state: the StoreOrderLimits "Limit Cart $ Amount" toggle
  disables when a per-shopper allowance is configured. That signal now
  comes from the v2 allowance-policy state (``allowanceActive``), fetched
  from GET /api/stores/<id>/allowance-policy/ on mount, NOT from the
  dropped legacy column.
-->
<template>
  <div class="store-checkout-orchestrator">
    <store-checkout-info
      ref="checkoutInfo"
      :campaign="campaign"
      :edit-mode="editMode"
      @set-department-enabled="forwardSetDepartmentEnabled"
      @set-manager-enabled="forwardSetManagerEnabled"
      @set-location-enabled="forwardSetLocationEnabled"
      @campaign-changed="forwardCampaignChanged"
      @checkout-fields-validate="forwardCheckoutFieldsValidate"
      @update-tab-invalid="forwardUpdateTabInvalid"
    />

    <editor-card title="Store Limits & Security">
      <store-order-limits
        ref="orderLimits"
        :campaign="campaign"
        :edit-mode="editMode"
        :disable-fields="disableForCampaignLifecycle"
        :storefront-pricing-enabled="storefrontPricingEnabled"
        :allowance-active="allowanceActive"
        @campaign-changed="forwardCampaignChanged"
        @update:price-limit-enabled="() => {}"
        @update:items-count-limit-enabled="() => {}"
      />
    </editor-card>

    <store-order-number-format
      ref="orderNumberFormat"
      :campaign="campaign"
      :edit-mode="editMode"
      @campaign-changed="forwardCampaignChanged"
    />
  </div>
</template>

<script>
import StoreCheckoutInfo from "./StoreCheckoutInfo.vue";
import StoreOrderLimits from "./StoreOrderLimits.vue";
import StoreOrderNumberFormat from "./StoreOrderNumberFormat.vue";
import EditorCard from "./components/EditorCard.vue";
import { computeDisableFlags } from "./disableFlags.js";

export default {
  name: "StoreCheckout",

  components: {
    "store-checkout-info": StoreCheckoutInfo,
    "store-order-limits": StoreOrderLimits,
    "store-order-number-format": StoreOrderNumberFormat,
    "editor-card": EditorCard,
  },

  props: {
    campaign: { type: Object, required: true },
    editMode: { type: Boolean, default: false },
  },

  emits: [
    "campaign-changed",
    "checkout-fields-validate",
    "set-department-enabled",
    "set-manager-enabled",
    "set-location-enabled",
    "update-tab-invalid",
  ],

  data() {
    return {
      // PR E: the OrderLimits cart-$-limit gate disables when a v2
      // per-shopper allowance policy is active. Sourced from the v2
      // allowance-policy endpoint (the same one StoreAllowancePolicy uses),
      // NOT the dropped ``company_allowance`` column.
      allowanceActive: false,
    };
  },

  computed: {
    /**
     * Mirrors the gate the inner StoreCheckoutInfo uses so the
     * Order Limits card locks at the same lifecycle points.
     */
    disableForCampaignLifecycle() {
      return computeDisableFlags({
        campaign: this.campaign,
        isNewOrderAdded: this.$store && this.$store.getters
          ? this.$store.getters.getIsNewOrderAdded
          : false,
      }).disableForCampaignLifecycle;
    },

    /**
     * Reads storefront_pricing from the live campaign so the inner
     * StoreOrderLimits child receives the correct prop.
     */
    storefrontPricingEnabled() {
      return !!(this.campaign && this.campaign.storefront_pricing);
    },

    storeId() {
      return this.campaign && this.campaign._jv
        ? this.campaign._jv.id
        : null;
    },
  },

  mounted() {
    this.loadAllowanceState();
  },

  methods: {
    /**
     * Fetch the v2 allowance policy so the Order Limits cart-$-limit gate
     * knows whether a per-shopper allowance is configured. Self-contained
     * read (mirrors StoreAllowancePolicy.load); a missing / inactive policy
     * leaves the gate open.
     */
    async loadAllowanceState() {
      if (!this.storeId || !this.$axios) {
        this.allowanceActive = false;
        return;
      }
      try {
        const resp = await this.$axios.get(
          `/api/stores/${this.storeId}/allowance-policy/`,
        );
        const policy = resp && resp.data && resp.data.policy;
        this.allowanceActive = !!(policy && policy.is_active !== false);
      } catch (e) {
        this.allowanceActive = false;
      }
    },

    // ----- Parent-facing API (matches the legacy StoreCheckoutInfo) -----

    /**
     * Save-time aggregator. Calls each child's updateCampaign / getData
     * and merges the result into the campaign payload.
     */
    updateCampaign(campaign) {
      campaign.error = null;
      // StoreCheckoutInfo writes back its slice directly onto the campaign.
      this.$refs.checkoutInfo?.updateCampaign(campaign);

      // StoreOrderLimits returns {items_count_limit, price_limit}.
      const limits = this.$refs.orderLimits?.getData() || {};
      Object.assign(campaign, limits);

      // StoreOrderNumberFormat returns the three order-number columns.
      const orderNumber = this.$refs.orderNumberFormat?.getData() || {};
      Object.assign(campaign, orderNumber);

      return campaign;
    },

    /**
     * Called by the parent on tab navigation + before save. Runs the
     * inner StoreCheckoutInfo validateTab (the field-set validity is
     * the gate); the limits card has no required fields.
     */
    validateTab() {
      const info = this.$refs.checkoutInfo;
      if (info && typeof info.validateTab === "function") {
        return info.validateTab();
      }
      return false;
    },

    /**
     * A store save was refused over the Checkout slice: the parent has put
     * the SAVED slice back on ``campaign``. Re-hydrate the inner card from
     * it and say why there (Bug Intake #450). StoreAddEdit's
     * ``checkoutInfo`` ref is this orchestrator, so it must forward.
     */
    adoptSavedCheckout(reason) {
      const info = this.$refs.checkoutInfo;
      if (info && typeof info.adoptSavedCheckout === "function") {
        info.adoptSavedCheckout(reason);
      }
    },

    loadComponent() {
      const info = this.$refs.checkoutInfo;
      if (info && typeof info.loadComponent === "function") {
        info.loadComponent();
      }
    },

    async getUsedCustomDropdownOptions() {
      const info = this.$refs.checkoutInfo;
      if (info && typeof info.getUsedCustomDropdownOptions === "function") {
        return info.getUsedCustomDropdownOptions();
      }
    },

    loadFromCampaign(campaign) {
      const info = this.$refs.checkoutInfo;
      if (info && typeof info.fillPreliminaryData === "function") {
        info.fillPreliminaryData();
      }
      const limits = this.$refs.orderLimits;
      if (limits && typeof limits.loadFromCampaign === "function") {
        limits.loadFromCampaign(campaign);
      }
      const orderNumber = this.$refs.orderNumberFormat;
      if (orderNumber && typeof orderNumber.loadFromCampaign === "function") {
        orderNumber.loadFromCampaign(campaign);
      }
      // Re-read the v2 allowance state whenever the store reloads.
      this.loadAllowanceState();
    },

    // ----- Inner event forwarders -----

    forwardCampaignChanged(...args) {
      this.$emit("campaign-changed", ...args);
    },

    forwardCheckoutFieldsValidate(valid) {
      this.$emit("checkout-fields-validate", valid);
    },

    forwardSetDepartmentEnabled(val) {
      this.$emit("set-department-enabled", val);
    },

    forwardSetManagerEnabled(val) {
      this.$emit("set-manager-enabled", val);
    },

    forwardSetLocationEnabled(val) {
      this.$emit("set-location-enabled", val);
    },

    /**
     * The inner StoreCheckoutInfo emits update-tab-invalid with the
     * legacy tab name "checkoutInfo"; the parent's incompleteTabs map
     * keys on that name. Forward the event unchanged.
     */
    forwardUpdateTabInvalid(...args) {
      this.$emit("update-tab-invalid", ...args);
    },
  },
};
</script>

<style scoped>
.store-checkout-orchestrator {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
